import { Container, Row, Col } from 'react-bootstrap';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/Page.css';
import SEO from './SEO';
import ContactForm from './ContactForm';
import { LoadingContext } from './context/LoadingContext';
import usePrefetch from '../hooks/usePrefetch';
import NotFound from './NotFound';
import hasStoreName from '../helpers/hasStoreName';
import AppContext from './context/AppContext';
import AbtraEditorContentDisplay from './AbtraEditorContentDisplay';
import PageSidebar from './PageSidebar';
import Breadcrumbs from './Breadcrumbs';
import PageForm from './PageForm';
import { ToastContext } from './context/ToastContext';
import Spinner from './Spinner';
import { bool } from 'prop-types';

// Page fetching func.
const fetchPage = async (handle = '') => {
    const response = await fetch(new URL(`/api/pages/handle/${handle}`, process.env.APP_URL));
    const data = await response.json();
    return data?.page ?? null;
};

const Page = ({ isMobile }) => {
    const { appContext } = useContext(AppContext);
    const { sendMessage } = useContext(ToastContext);
    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const { handle } = useParams();
    const [loading, setLoading] = useState(false);
    const [pageForms, setPageForms] = useState([]);

    const {
        isLoaded: pageDataLoaded,
        data: pageData,
    } = usePrefetch({
        key: 'page',
        resourceKey: 'handle',
        resourceValue: handle,
        prefetchFn: async () => {
            await startLoading();
            const data = await fetchPage(handle);
            await finishLoading();
            return data;
        }
    });

    const fetchPageForms = useCallback(async () => {
        try {
            setLoading(true);
            const url = new URL(`/api/pages/handle/${handle}/forms`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            });
            const { forms, error } = await response.json();

            if (error) {
                sendMessage(error, 'danger');
            }

            if (forms) {
                setPageForms(forms);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    }, [handle]);

    useEffect(() => {
        if (pageDataLoaded && pageData) {
            fetchPageForms();
        }
    }, [pageDataLoaded, pageData, fetchPageForms]);

    /** Add store name to title if not exists */
    const title = useMemo(() => {
        if (!pageDataLoaded || !pageData || !Object.keys(pageData).length > 0) {
            return '';
        }
        let title = pageData.title;
        if (!hasStoreName(title, appContext.config.storeName)) {
            title = `${title} - ${appContext.config.storeName}`;
        }
        return title;
    }, [pageDataLoaded, pageData]);

    // Generate breadcrumb paths
    const breadcrumbPaths = useMemo(() => {
        const paths = [];

        if (pageDataLoaded && pageData) {
            paths.push([
                pageData.title,
                `/${handle}`
            ]);
        }

        return paths;
    }, [pageDataLoaded, pageData, handle]);

    if (pageDataLoaded === true && (!pageData || Object.keys(pageData ?? {}).length === 0)) {
        return (<NotFound />);
    }
    return (
        <>
            {pageDataLoaded && Object.keys(pageData ?? {}).length > 0 && (
                <>
                    <SEO
                        seoTitle={title}
                        seoType={'website'}
                        canonicalUrl={new URL(`/pages/${pageData.handle}`, process.env.APP_URL).toString()}
                    />
                    <div
                        className={'page--section'}
                    >
                        <Breadcrumbs
                            breadcrumbList={breadcrumbPaths}
                        />
                    </div>
                </>
            )}
            <Container
                fluid
            >
                <div
                    className={'section'}
                >
                    {!pageDataLoaded && (
                        <p>Fetching page...</p>
                    )}
                    {pageDataLoaded && Object.keys(pageData ?? {}).length <= 0 && (
                        <p>Page does not exist.</p>
                    )}
                    {pageDataLoaded && Object.keys(pageData ?? {}).length > 0 && (
                        <Row
                            className={'page--with-sidebar'}
                        >
                            <Col
                                lg={3}
                                md={4}
                                className={'page--sidebar-container'}
                            >
                                <PageSidebar />
                            </Col>
                            <Col
                                lg={9}
                                md={8}
                            >
                                {handle !== 'contact-us' ? (
                                    <>
                                        <div>
                                            <h1
                                                className={'page-content--title'}
                                            >
                                                {pageData.title}
                                            </h1>
                                        </div>
                                        <div
                                            className={'page-content--body'}
                                        >
                                            {loading ? (
                                                <Spinner />
                                            ) : (
                                                pageForms?.map((form) => (
                                                    <PageForm
                                                        key={form.id}
                                                        form={form}
                                                        isMobile={isMobile}
                                                    />
                                                ))
                                            )}
                                            <AbtraEditorContentDisplay
                                                htmlContent={pageData.bodyHtml}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className={'page-content--body'}
                                    >
                                        <h1
                                            className={'page-content--title'}
                                        >
                                            {pageData.title}
                                        </h1>
                                        <ContactForm />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    )}
                </div>
            </Container>
        </>
    );
};

Page.propTypes = {
    isMobile: bool,
};

export default Page;